/**
 * Daily quests: every day the notebook picks one quest from each group, so
 * there is always something to fight, a move to practise and something to
 * make or share. Progress is the change in the save's counters since the day
 * began (see systems/daily.ts).
 */
import type { Counters } from '../systems/model';

export type DailyGroup = 'fight' | 'move' | 'make';

export interface DailyDef {
  id: string;
  group: DailyGroup;
  title: string;
  note: string;
  metric: keyof Counters;
  goal: number;
  coins: number;
  exp: number;
  /** Needs the battle server (voting, sharing, online battles). */
  online?: true;
  /** Needs a Hall of Fame monster in the notebook. */
  hall?: true;
  /** Needs a friend's dare in the notebook. */
  dare?: true;
  /** Needs this week's boss in the notebook. */
  boss?: true;
}

export const DAILY_QUESTS: DailyDef[] = [
  { id: 'win_3', group: 'fight', title: 'Recess Champion', note: 'Win 3 battles today.', metric: 'wins', goal: 3, coins: 15, exp: 20 },
  { id: 'battle_4', group: 'fight', title: 'Busy Playground', note: 'Fight 4 battles today.', metric: 'battles', goal: 4, coins: 10, exp: 20 },
  { id: 'three_star', group: 'fight', title: 'Gold Star Day', note: 'Win a story battle with 3 stars.', metric: 'threeStarWins', goal: 1, coins: 20, exp: 20 },
  { id: 'hall_win', group: 'fight', title: 'Beat a Champion', note: 'Defeat a Hall of Fame monster.', metric: 'hallWins', goal: 1, coins: 20, exp: 25, hall: true },
  { id: 'boss_1', group: 'fight', title: 'Boss Raid', note: 'Fight the boss of the week once.', metric: 'bossBattles', goal: 1, coins: 15, exp: 20, boss: true },
  { id: 'dare_beat_1', group: 'fight', title: 'Dare Accepted', note: "Beat a monster a friend dared you with.", metric: 'daresBeaten', goal: 1, coins: 20, exp: 25, dare: true },
  { id: 'rock_4', group: 'move', title: 'Rock Practice', note: 'Win 4 rounds with Rock today.', metric: 'rockWins', goal: 4, coins: 10, exp: 15 },
  { id: 'paper_4', group: 'move', title: 'Paper Practice', note: 'Win 4 rounds with Paper today.', metric: 'paperWins', goal: 4, coins: 10, exp: 15 },
  { id: 'scissors_4', group: 'move', title: 'Scissors Practice', note: 'Win 4 rounds with Scissors today.', metric: 'scissorsWins', goal: 4, coins: 10, exp: 15 },
  { id: 'draw_1', group: 'make', title: 'Doodle of the Day', note: 'Draw and save a new monster.', metric: 'monstersCreated', goal: 1, coins: 15, exp: 20 },
  { id: 'vote_1', group: 'make', title: 'Contest Judge', note: 'Vote for a monster in the contest.', metric: 'votes', goal: 1, coins: 10, exp: 15, online: true },
  { id: 'share_1', group: 'make', title: 'Show and Tell Day', note: 'Share a monster with a friend.', metric: 'shares', goal: 1, coins: 10, exp: 15, online: true },
  { id: 'pvp_1', group: 'make', title: 'Battle a Classmate', note: 'Play one online battle.', metric: 'pvpBattles', goal: 1, coins: 15, exp: 20, online: true },
  { id: 'dare_send_1', group: 'make', title: 'Pass the Notebook', note: 'Send a friend a monster to beat.', metric: 'daresSent', goal: 1, coins: 15, exp: 20, online: true },
  { id: 'sticker_1', group: 'make', title: 'Fan Mail', note: "Send a sticker to the artist of a monster you fought.", metric: 'reactionsSent', goal: 1, coins: 10, exp: 15, online: true },
];

export const DAILY_GROUPS: DailyGroup[] = ['fight', 'move', 'make'];

/** Coins for finishing all three in a day, plus this much per day of streak (capped). */
export const DAILY_BONUS = { base: 20, perStreakDay: 5, maxStreakDays: 7 };

export function dailyDef(id: string): DailyDef | undefined {
  return DAILY_QUESTS.find((q) => q.id === id);
}
